import { Link } from 'react-router-dom';
import { AlertTriangle, Check } from 'lucide-react';
import type { Etapa, Situacao } from './situacao';

export type Passo = 'cotacao' | 'propostas' | 'apurar' | 'equalizacao' | 'analise' | 'fechamento';

type Estado = 'feito' | 'parcial' | 'atencao' | 'pendente';

type CotacaoDosPassos = {
  id: number;
  situacao: Situacao;
  rodadas: { id: number; numero: number; propostas: unknown[] }[];
  status?: string;
  origem?: { id: number; numero: number } | null;
};

/** Para onde o "próximo passo" de cada etapa aponta — o mesmo botão da listagem. */
const PROXIMO: Record<Etapa, Passo> = {
  aguardando_propostas: 'propostas',
  conferir_equalizacao: 'equalizacao',
  negociar: 'analise',
  aguardando_revisao: 'propostas',
  fechada: 'fechamento',
};

const ROTA: Record<Passo, string> = {
  cotacao: '',
  propostas: '/propostas',
  apurar: '/propostas',
  equalizacao: '/equalizacao',
  analise: '/analise',
  fechamento: '/fechamento',
};

const BARRA: Record<Estado, string> = {
  feito: 'border-t-success',
  parcial: 'border-t-success/50',
  atencao: 'border-t-warning',
  pendente: 'border-t-border',
};

/**
 * O caminho de uma cotação, sempre à vista no topo das telas dela:
 * Cotação → Propostas dos fornecedores → Apurar cotações → Equalização →
 * Análise dos resultados → Fechamento. Cada bloco leva à sua tela, mostra como está e o
 * próximo passo vem marcado — o comprador não precisa adivinhar onde clicar.
 * A análise é onde se abre a rodada de negociação, que recomeça pelas propostas.
 */
export default function PassosDaCotacao({
  cotacao,
  atual,
  rodadaId = null,
}: {
  cotacao: CotacaoDosPassos;
  atual: Passo;
  rodadaId?: number | null;
}) {
  const s = cotacao.situacao;
  // A cotação separada nasce com os preços da original: já dá para equalizar.
  const temProposta = Boolean(cotacao.origem) || cotacao.rodadas.some((rodada) => rodada.propostas.length > 0);
  const fechada = cotacao.status === 'fechada';
  const total = s.fornecedores.length;
  const nomeDaRodada = s.rodada.nome.toLowerCase();
  const proximo = PROXIMO[s.etapa];

  const passos: { id: Passo; rotulo: string; detalhe: string; estado: Estado; bloqueado?: string }[] = [
    { id: 'cotacao', rotulo: 'Cotação', detalhe: 'Itens e planilha para os fornecedores', estado: 'feito' },
    {
      id: 'propostas',
      rotulo: 'Propostas dos fornecedores',
      detalhe: `${s.respostas} de ${total} na ${nomeDaRodada}`,
      estado: total > 0 && s.respostas >= total ? 'feito' : s.respostas > 0 ? 'parcial' : 'pendente',
    },
    {
      id: 'apurar',
      rotulo: 'Apurar cotações',
      detalhe:
        s.respostas === 0
          ? 'Escolha os arquivos e apure'
          : s.respostas === 1
            ? '1 proposta apurada'
            : `${s.respostas} propostas apuradas`,
      estado: s.respostas > 0 ? 'feito' : 'pendente',
    },
    {
      id: 'equalizacao',
      rotulo: 'Equalização',
      detalhe: s.equalizacao === 'em_dia' ? 'Conferida' : s.equalizacao === 'desatualizada' ? 'Mudou: veja de novo' : 'A conferir',
      estado: s.equalizacao === 'em_dia' ? 'feito' : s.equalizacao === 'desatualizada' ? 'atencao' : 'pendente',
      bloqueado: temProposta ? undefined : 'Apure ao menos uma proposta antes',
    },
    {
      id: 'analise',
      rotulo: 'Análise dos resultados',
      detalhe:
        s.etapa === 'negociar'
          ? 'Pronta para negociar'
          : s.etapa === 'aguardando_revisao'
            ? 'Aguardando nova proposta'
            : fechada
              ? 'Concluída'
              : 'Alvo, argumentos e nova rodada',
      // Fechado o pacote, a análise ficou para trás: é passo cumprido.
      estado: fechada ? 'feito' : 'pendente',
      bloqueado: temProposta ? undefined : 'Apure ao menos uma proposta antes',
    },
    {
      id: 'fechamento',
      rotulo: 'Fechamento',
      detalhe: fechada ? 'Pacote fechado' : 'Escolha com quem fechar o pacote',
      estado: fechada ? 'feito' : 'pendente',
      bloqueado: temProposta ? undefined : 'Apure ao menos uma proposta antes',
    },
  ];

  return (
    <nav aria-label="Etapas da cotação" className="mb-lg overflow-hidden rounded-lg border border-border bg-surface">
      <ol className="grid sm:grid-cols-3 lg:grid-cols-6">
        {passos.map((passo, indice) => {
          const aqui = passo.id === atual;
          const ehProximo = passo.id === proximo && !aqui;
          // Apurar mora na tela de propostas; o ?etapa=apurar diz a ela qual bloco acender.
          const busca = new URLSearchParams();
          if (rodadaId && passo.id !== 'cotacao') busca.set('rodada', String(rodadaId));
          if (passo.id === 'apurar') busca.set('etapa', 'apurar');
          const destino = `/cotacoes/${cotacao.id}${ROTA[passo.id]}${busca.toString() ? `?${busca.toString()}` : ''}`;

          const conteudo = (
            <>
              <span
                className={[
                  'flex size-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold',
                  passo.estado === 'feito'
                    ? 'bg-success text-white'
                    : passo.estado === 'atencao'
                      ? 'bg-warning text-white'
                      : aqui
                        ? 'bg-accent text-white'
                        : passo.estado === 'parcial'
                          ? 'border-2 border-success text-success'
                          : 'border border-border text-muted',
                ].join(' ')}
                aria-hidden="true"
              >
                {passo.estado === 'feito' ? <Check size={15} /> : passo.estado === 'atencao' ? <AlertTriangle size={13} /> : indice + 1}
              </span>
              <span className="min-w-0">
                <span className={`block text-[13px] leading-tight font-bold ${aqui ? 'text-accent' : passo.bloqueado ? 'text-muted' : 'text-ink'}`}>
                  {passo.rotulo}
                </span>
                <span className="mt-[2px] block text-[11px] leading-snug text-muted">{passo.bloqueado ?? passo.detalhe}</span>
                {ehProximo && !passo.bloqueado && (
                  <span className="mt-xs inline-block rounded-full bg-accent/10 px-sm py-[1px] text-[10px] font-bold tracking-wide text-accent uppercase">
                    Próximo passo
                  </span>
                )}
              </span>
            </>
          );

          const classe = [
            'flex h-full items-start gap-sm border-t-[3px] px-md py-sm transition-colors',
            aqui ? 'border-t-accent bg-accent/5' : BARRA[passo.estado],
            indice > 0 ? 'sm:border-l sm:border-l-border' : '',
          ].join(' ');

          return (
            <li key={passo.id} className="border-b border-border last:border-b-0 sm:border-b-0">
              {passo.bloqueado ? (
                <span className={`${classe} cursor-not-allowed opacity-70`} aria-disabled="true">
                  {conteudo}
                </span>
              ) : (
                <Link to={destino} aria-current={aqui ? 'step' : undefined} className={`${classe} hover:bg-surface-subtle`}>
                  {conteudo}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
